import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { IGeneralServiceProviderFilter } from "./interface";
import { RootState } from "../../../../redux/store";
import { EndPoints } from "../../../../Constants/endpoints";
import { useGetData } from "../../../../hooks";
import { Option } from "../../../../interface/OptionTypes";
import i18n from "../../../../i18n";
import {
  Drawer,
  GenderRadioSelect,
  GeneralButton,
  GeneralRadio,
  GeneralSelectApi,
} from "../../../../components";

const GeneralServiceProviderFilter: React.FC<IGeneralServiceProviderFilter> = ({
  isOpen,
  onClose,
  onSubmit,
  onReset,
  methods,
  TypeId,
  setTags,
  title,
  allParams,
  AccountTypeId,
}) => {
  const location = useLocation();

  const { t } = useTranslation();
  const CountryId = useSelector(
    (state: RootState) => state.registration.userDetails.CountryId,
  );
  const currentLocationParams = new URLSearchParams(location.search);
  const getAllParams = allParams ?? Object.fromEntries(currentLocationParams);

  const { data: selectedAreaData, isSuccess: isAreaSuccess } = useGetData<any>(
    EndPoints.GetArea,
    {
      enabled: !!getAllParams?.AreaId,
    },
    getAllParams?.AreaId,
  );

  const { data: selectedCityData, isSuccess: isCitySuccess } = useGetData<any>(
    EndPoints.GetCities,
    { enabled: !!getAllParams?.CityId },
    getAllParams?.CityId,
  );

  const { data: selectedSpecialtyData, isSuccess: isSpecialtySuccess } =
    useGetData<any>(
      EndPoints.GetSpecialties,
      { enabled: !!getAllParams?.SpecilaityId },
      getAllParams?.SpecilaityId,
    );

  const getDataAsOption = (data: any) => {
    return { value: data?.Id, label: data?.Name, key: data?.Id };
  };

  const [onlyWhoSetPrice, setOnlyWhoSetPrice] = useState<boolean>(false);

  const [selectedOptions, setSelectedOptions] = useState<any>({
    CityId: getDataAsOption(selectedCityData?.Data),
    SpecilaityId: getDataAsOption(selectedSpecialtyData?.Data),
    AreaId: getDataAsOption(selectedAreaData?.Data),
  });
  const [selectedCity, setSelectedCity] = useState<Option | undefined>(
    getDataAsOption(selectedCityData?.Data),
  );

  const { handleSubmit, setValue } = methods;

  const filterKeys = [
    "facility",
    "search-value",
    "SpecilaityId",
    "CityId",
    "AreaId",
    "Gender",
    "PriceEnd",
    "PriceSet",
  ];

  const defaultFiltersValues = {
    ...getAllParams,
    CityId: getAllParams?.CityId
      ? (getDataAsOption(selectedCityData?.Data)?.label ?? getAllParams?.CityId)
      : null,
    SpecilaityId:
      getDataAsOption(selectedSpecialtyData?.Data)?.label ??
      getAllParams?.SpecialityId,
    AreaId:
      getDataAsOption(selectedAreaData?.Data)?.label ?? getAllParams?.AreaId,
  };

  useEffect(() => {
    const allTags = Object.entries(defaultFiltersValues ?? {})?.map(
      ([key, value]) => {
        return key != "facility" &&
          value &&
          filterKeys?.includes(key) &&
          !(key == "PriceEnd" && value == 200000)
          ? {
              label:
                key == "Gender"
                  ? t(`${value}`?.toLowerCase())
                  : key == "PriceSet"
                    ? value
                      ? t("only_who_set_price")
                      : undefined
                    : value,
              value: value,
              key: key,
            }
          : undefined;
      },
    );
    setTags(allTags?.filter(Boolean));
    setOnlyWhoSetPrice(getAllParams?.PriceSet);
  }, [
    location.search,
    selectedCity,
    allParams,
    isAreaSuccess,
    isCitySuccess,
    isSpecialtySuccess,
    i18n?.language,
    selectedOptions,
  ]);

  const handleChange = (e?: any) => {
    setValue("PriceSet", e?.target.name === "PriceSet");
    if (e?.target.name === "PriceSet") setOnlyWhoSetPrice(true);
    else setOnlyWhoSetPrice(false);
  };

  return (
    <Drawer
      isOpen={isOpen}
      customStyle="w-[402px]"
      header={title}
      onClose={onClose}
    >
      <div className="flex h-full flex-col justify-between">
        <FormProvider {...methods}>
          <form
            className="flex h-full w-full flex-col gap-16"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-4 px-4">
              {(TypeId === "2" || TypeId === "3") && (
                <GeneralSelectApi
                  endpoint={EndPoints.Specialties}
                  allParams={{ AccountTypeId: AccountTypeId }}
                  name="SpecilaityId"
                  placeholder={t("specialty")}
                  defaultValue={selectedOptions?.SpecilaityId}
                  onChange={(value) => {
                    setSelectedOptions((prev: any) => {
                      return {
                        ...prev,
                        SpecilaityId: value?.label,
                      };
                    });
                  }}
                />
              )}
              <GeneralSelectApi
                name="CityId"
                placeholder={t("city")}
                endpoint={EndPoints.Cities}
                allParams={{ CountryId: CountryId }}
                defaultValue={selectedOptions?.CityId}
                onChange={(value) => {
                  setSelectedCity(value);
                  setValue("AreaId", undefined);
                  setSelectedOptions((prev: any) => {
                    return {
                      ...prev,
                      CityId: value?.label,
                    };
                  });
                }}
              />

              <GeneralSelectApi
                name="AreaId"
                placeholder={t("area")}
                isDisabled={!selectedCity?.value}
                allParams={{ cityId: Number(selectedCity?.value) ?? null }}
                enabled={Number(selectedCity?.value) ? true : false}
                endpoint={EndPoints.Area}
                defaultValue={selectedOptions?.AreaId}
                onChange={(value) => {
                  setSelectedOptions((prev: any) => {
                    return {
                      ...prev,
                      AreaId: value?.label,
                    };
                  });
                }}
              />
              {(TypeId === "2" || TypeId === "3") && (
                <>
                  <GenderRadioSelect required={false} name={"Gender"} />
                  {/* <RangeSlider
                    name="PriceEnd"
                    label={t("maximum_price")}
                    unit={t("syp")}
                    step={50}
                    min={100}
                    max={20000}
                  /> */}
                  <div className="mt-10 flex justify-between">
                    <GeneralRadio
                      name="All"
                      checked={!onlyWhoSetPrice}
                      label={t("all_doctors")}
                      onClick={handleChange}
                    />
                    <GeneralRadio
                      name="PriceSet"
                      checked={onlyWhoSetPrice}
                      label={t("only_who_set_price")}
                      onClick={handleChange}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 px-4">
              <GeneralButton
                title={t("reset")}
                onClick={() => {
                  setSelectedCity(undefined);
                  setSelectedOptions({});
                  setOnlyWhoSetPrice(false);
                  onClose();
                  onReset();
                }}
                color="primaryBg"
                className="!w-1/2"
              />
              <GeneralButton
                type="submit"
                className="!w-1/2"
                title={`${t("apply_filter")}`}
              />
            </div>
          </form>
        </FormProvider>
      </div>
    </Drawer>
  );
};

export default GeneralServiceProviderFilter;
